import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Input, InputNumber, Modal, Space, Tag, message } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import BadgeCardSheet from "components/shared-components/Print/BadgeCardSheet";
import { BADGE_PLAN, nextBadgeNumber, parseBadgeLines, planBadgeRegistration } from "functions/badgeCards";
import { A4_PAGE_STYLE } from "../shared";

const KIND_COLORS = { [BADGE_PLAN.NEW]: "green", [BADGE_PLAN.REPRINT]: "default", [BADGE_PLAN.REACTIVATE]: "blue" };

/**
 * Picker badges (HVC-54): one name per line, registered in `pickers/{code}` and then printed eight
 * to an A4. Registering comes first, so every printed badge is in the register; a code that someone
 * else registered in the meantime stops the print (usePickers.registerBadges).
 *
 * Stays mounted while closed: the print runs after the window has closed.
 */
const BadgePrintModal = ({ open, initialText = "", pickers, registerError, registerBadges, onClose }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(initialText);
  const [start, setStart] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setText(initialText);
    setStart(null);
  }, [open, initialText]);

  const taken = useMemo(() => (pickers || []).map((picker) => picker.code), [pickers]);
  const startNumber = start || nextBadgeNumber(pickers);
  const parsed = useMemo(() => parseBadgeLines(text, { startNumber, taken }), [text, startNumber, taken]);
  const plan = useMemo(() => planBadgeRegistration(parsed.cards, pickers), [parsed.cards, pickers]);
  const problems = [
    ...parsed.problems.map((problem) =>
      t(problem.reason === "duplicate-code" ? "orderpicking.pickers.badges_problem_duplicate" : "orderpicking.pickers.badges_problem_invalid", problem),
    ),
    ...plan.problems.map((problem) =>
      t(problem.reason === "name-mismatch" ? "orderpicking.pickers.badges_problem_mismatch" : "orderpicking.pickers.badges_problem_no_name", problem),
    ),
  ];

  const print = useReactToPrint({
    contentRef: sheetRef,
    documentTitle: "Pickerpasjes",
    pageStyle: A4_PAGE_STYLE,
    onAfterPrint: () => setJob(null),
  });
  useEffect(() => {
    if (job) print();
  }, [job, print]);

  const handleOk = async () => {
    setBusy(true);
    try {
      await registerBadges(plan.cards);
      message.success(t("orderpicking.pickers.badges_registered", { count: plan.cards.length }), 4);
      setJob(plan.cards);
      onClose();
    } catch (error) {
      const takenCode = /^pickers\/taken:(.+)$/.exec(error.message || "");
      message.error(
        takenCode
          ? t("orderpicking.pickers.badges_taken", { code: takenCode[1] })
          : t("orderpicking.pickers.badges_register_failed", { message: error.message }),
        6,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        title={t("orderpicking.pickers.badges_title")}
        okText={t("orderpicking.pickers.badges_print")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !!registerError || !plan.ok || problems.length > 0, loading: busy, icon: <PrinterOutlined /> }}
        onCancel={onClose}
        onOk={handleOk}
      >
        <p style={{ color: "#6b6b6b" }}>{t("orderpicking.pickers.badges_help")}</p>
        {!!registerError && <Alert type="error" showIcon className="mb-3" message={t("orderpicking.pickers.register_error")} description={t("orderpicking.pickers.register_error_help")} />}
        <div style={{ marginBottom: 8 }}>{t("orderpicking.pickers.badges_names")}</div>
        <Input.TextArea rows={7} value={text} onChange={(event) => setText(event.target.value)} placeholder={"Dick\nSieger\nBADGE-0042"} />
        <Space style={{ marginTop: 12 }}>
          <span>{t("orderpicking.pickers.badges_start")}</span>
          <InputNumber min={1} max={9999} value={startNumber} onChange={(value) => setStart(value || null)} />
          <Tag>{t("orderpicking.pickers.badges_count", { count: plan.cards.length })}</Tag>
        </Space>
        {plan.cards.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {plan.cards.map((card) => (
              <div key={card.code}>
                <code>{card.code}</code> {card.name} <Tag color={KIND_COLORS[card.kind]}>{t(`orderpicking.pickers.kind_${card.kind}`)}</Tag>
              </div>
            ))}
          </div>
        )}
        {problems.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={
              <div>
                {problems.map((problem, index) => (
                  <div key={index}>{problem}</div>
                ))}
              </div>
            }
          />
        )}
      </Modal>
      {job && (
        <div style={{ display: "none" }}>
          <BadgeCardSheet ref={sheetRef} cards={job} />
        </div>
      )}
    </>
  );
};

export default BadgePrintModal;
